import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FolderOpen,
  FileText,
  Upload,
  Download,
  Search,
  Plus,
  User,
  Calendar,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fileCategories = [
  { id: 'identification', name: 'Identification Documents' },
  { id: 'right_to_work', name: 'Right to Work' },
  { id: 'dbs', name: 'DBS Certificates' },
  { id: 'qualifications', name: 'Qualifications & Certificates' },
  { id: 'training', name: 'Training Records' },
  { id: 'references', name: 'References' },
  { id: 'contracts', name: 'Contracts & Agreements' },
  { id: 'health', name: 'Health Records' },
  { id: 'supervision', name: 'Supervision Records' },
  { id: 'appraisal', name: 'Appraisal Records' },
  { id: 'disciplinary', name: 'Disciplinary Records' },
  { id: 'grievance', name: 'Grievance Records' },
  { id: 'other', name: 'Other Documents' },
];

const templateTypes = [
  { id: 'supervision', name: 'Supervision', icon: ClipboardList },
  { id: 'appraisal', name: 'Appraisal', icon: Calendar },
  { id: 'induction', name: 'Induction', icon: User },
  { id: 'probation', name: 'Probation Review', icon: Clock },
  { id: 'disciplinary', name: 'Disciplinary', icon: AlertTriangle },
  { id: 'grievance', name: 'Grievance', icon: FileText },
  { id: 'return_to_work', name: 'Return to Work', icon: CheckCircle },
  { id: 'exit_interview', name: 'Exit Interview', icon: User },
];

export default function HRSuite() {
  const { user, organization } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [personnelFiles, setPersonnelFiles] = useState([]);
  const [hrRecords, setHrRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [uploadData, setUploadData] = useState({
    staff_id: '',
    category: '',
    description: '',
    expiry_date: '',
    is_gdpr_sensitive: false,
    file: null,
  });

  const [recordData, setRecordData] = useState({
    staff_id: '',
    template_type: '',
    conducted_date: new Date().toISOString().split('T')[0],
    next_due_date: '',
    outcome: '',
    action_points: '',
    data: {},
  });

  useEffect(() => {
    fetchData();
  }, [organization]);

  const fetchData = async () => {
    try {
      const [staffRes, filesRes, recordsRes, templatesRes, defaultsRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/hr/personnel-files`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/hr/records`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/hr/templates`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/hr/templates/defaults`),
      ]);
      
      setStaff(staffRes.data);
      setPersonnelFiles(filesRes.data);
      setHrRecords(recordsRes.data);
      setTemplates(templatesRes.data);
      setDefaultTemplates(defaultsRes.data);
    } catch (error) {
      console.error('Error fetching HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.staff_id || !uploadData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('staff_id', uploadData.staff_id);
    formData.append('organization_id', organization?.id);
    formData.append('category', uploadData.category);
    formData.append('description', uploadData.description);
    formData.append('expiry_date', uploadData.expiry_date);
    formData.append('is_gdpr_sensitive', uploadData.is_gdpr_sensitive);

    try {
      await axios.post(`${API_URL}/api/hr/personnel-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded successfully');
      setIsUploadOpen(false);
      setUploadData({ staff_id: '', category: '', description: '', expiry_date: '', is_gdpr_sensitive: false, file: null });
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const handleCreateRecord = async (e) => {
    e.preventDefault();
    try {
      const template = defaultTemplates.find(t => t.template_type === recordData.template_type);
      
      await axios.post(`${API_URL}/api/hr/records`, {
        staff_id: recordData.staff_id,
        organization_id: organization?.id,
        template_id: template?.id || recordData.template_type,
        template_type: recordData.template_type,
        data: recordData.data,
        conducted_by: user?.id,
        conducted_date: recordData.conducted_date,
        next_due_date: recordData.next_due_date,
        outcome: recordData.outcome,
        action_points: recordData.action_points ? recordData.action_points.split('\n').filter(a => a.trim()) : [],
      });
      
      toast.success('HR record created successfully');
      setIsRecordOpen(false);
      setRecordData({
        staff_id: '',
        template_type: '',
        conducted_date: new Date().toISOString().split('T')[0],
        next_due_date: '',
        outcome: '',
        action_points: '',
        data: {},
      });
      fetchData();
    } catch (error) {
      console.error('Error creating record:', error);
      toast.error('Failed to create record');
    }
  };

  const getStaffName = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    if (staffMember?.user) {
      return `${staffMember.user.first_name} ${staffMember.user.last_name}`;
    }
    return 'Unknown';
  };

  const filteredStaff = staff.filter(s => {
    const name = getStaffName(s.id).toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const getComplianceStats = () => {
    const total = staff.length;
    const dbsComplete = staff.filter(s => s.dbs_check_date).length;
    const rtwComplete = staff.filter(s => s.right_to_work_verified).length;
    const supervisionsDue = staff.filter(s => {
      if (!s.next_supervision_due) return true;
      return new Date(s.next_supervision_due) <= new Date();
    }).length;
    
    return { total, dbsComplete, rtwComplete, supervisionsDue };
  };

  const stats = getComplianceStats();

  return (
    <div className="min-h-screen" data-testid="hr-suite-page">
      <Header title="HR Suite" />
      
      <div className="p-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Staff</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">DBS Compliant</p>
                  <p className="text-3xl font-bold text-green-600">{stats.dbsComplete}/{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">RTW Verified</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.rtwComplete}/{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Supervisions Due</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.supervisionsDue}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="personnel-files">Personnel Files</TabsTrigger>
            <TabsTrigger value="hr-records">HR Records</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search staff..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="staff-search"
                />
              </div>
              <div className="flex gap-2">
                <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="upload-file-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Upload Personnel File</DialogTitle>
                      <DialogDescription>Upload a document to a staff member's personnel file</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleFileUpload} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Staff Member *</Label>
                        <Select value={uploadData.staff_id} onValueChange={(v) => setUploadData(prev => ({ ...prev, staff_id: v }))}>
                          <SelectTrigger data-testid="upload-staff-select">
                            <SelectValue placeholder="Select staff" />
                          </SelectTrigger>
                          <SelectContent>
                            {staff.map(s => (
                              <SelectItem key={s.id} value={s.id}>{getStaffName(s.id)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={uploadData.category} onValueChange={(v) => setUploadData(prev => ({ ...prev, category: v }))}>
                          <SelectTrigger data-testid="upload-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {fileCategories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>File *</Label>
                        <Input
                          type="file"
                          onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                          data-testid="upload-file-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={uploadData.description}
                          onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Brief description"
                          data-testid="upload-description"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Expiry Date</Label>
                        <Input
                          type="date"
                          value={uploadData.expiry_date}
                          onChange={(e) => setUploadData(prev => ({ ...prev, expiry_date: e.target.value }))}
                          data-testid="upload-expiry"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="gdpr_sensitive"
                          checked={uploadData.is_gdpr_sensitive}
                          onChange={(e) => setUploadData(prev => ({ ...prev, is_gdpr_sensitive: e.target.checked }))}
                        />
                        <Label htmlFor="gdpr_sensitive" className="text-sm">GDPR Sensitive Data</Label>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-upload">Upload</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={isRecordOpen} onOpenChange={setIsRecordOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-record-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New HR Record
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create HR Record</DialogTitle>
                      <DialogDescription>Create a new supervision, appraisal, or other HR record</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateRecord} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Staff Member *</Label>
                          <Select value={recordData.staff_id} onValueChange={(v) => setRecordData(prev => ({ ...prev, staff_id: v }))}>
                            <SelectTrigger data-testid="record-staff-select">
                              <SelectValue placeholder="Select staff" />
                            </SelectTrigger>
                            <SelectContent>
                              {staff.map(s => (
                                <SelectItem key={s.id} value={s.id}>{getStaffName(s.id)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Record Type *</Label>
                          <Select value={recordData.template_type} onValueChange={(v) => setRecordData(prev => ({ ...prev, template_type: v }))}>
                            <SelectTrigger data-testid="record-type-select">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {templateTypes.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date Conducted *</Label>
                          <Input
                            type="date"
                            value={recordData.conducted_date}
                            onChange={(e) => setRecordData(prev => ({ ...prev, conducted_date: e.target.value }))}
                            required
                            data-testid="record-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Next Due Date</Label>
                          <Input
                            type="date"
                            value={recordData.next_due_date}
                            onChange={(e) => setRecordData(prev => ({ ...prev, next_due_date: e.target.value }))}
                            data-testid="record-next-due"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Outcome</Label>
                        <Select value={recordData.outcome} onValueChange={(v) => setRecordData(prev => ({ ...prev, outcome: v }))}>
                          <SelectTrigger data-testid="record-outcome">
                            <SelectValue placeholder="Select outcome" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="satisfactory">Satisfactory</SelectItem>
                            <SelectItem value="good">Good</SelectItem>
                            <SelectItem value="excellent">Excellent</SelectItem>
                            <SelectItem value="requires_improvement">Requires Improvement</SelectItem>
                            <SelectItem value="unsatisfactory">Unsatisfactory</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Action Points (one per line)</Label>
                        <Textarea
                          value={recordData.action_points}
                          onChange={(e) => setRecordData(prev => ({ ...prev, action_points: e.target.value }))}
                          rows={4}
                          placeholder="Enter action points, one per line..."
                          data-testid="record-actions"
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsRecordOpen(false)}>Cancel</Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-record">Create Record</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Staff List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStaff.map((staffMember) => {
                const staffFiles = personnelFiles.filter(f => f.staff_id === staffMember.id);
                const staffRecords = hrRecords.filter(r => r.staff_id === staffMember.id);
                
                return (
                  <Card key={staffMember.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`staff-card-${staffMember.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                            <User className="w-6 h-6 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{getStaffName(staffMember.id)}</p>
                            <p className="text-sm text-slate-500">{staffMember.job_title}</p>
                          </div>
                        </div>
                        <Badge variant={staffMember.status === 'active' ? 'default' : 'secondary'}>
                          {staffMember.status}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Personnel Files:</span>
                          <span className="font-medium">{staffFiles.length}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">HR Records:</span>
                          <span className="font-medium">{staffRecords.length}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">DBS:</span>
                          {staffMember.dbs_check_date ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" /> Valid
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Missing
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Right to Work:</span>
                          {staffMember.right_to_work_verified ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" /> Verified
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Not Verified
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Personnel Files Tab */}
          <TabsContent value="personnel-files" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personnel Files</CardTitle>
                <CardDescription>All uploaded staff documents</CardDescription>
              </CardHeader>
              <CardContent>
                {personnelFiles.length > 0 ? (
                  <div className="space-y-3">
                    {personnelFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{file.file_name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <span>{getStaffName(file.staff_id)}</span>
                              <span>•</span>
                              <span className="capitalize">{file.category?.replace('_', ' ')}</span>
                              {file.is_gdpr_sensitive && (
                                <>
                                  <span>•</span>
                                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">GDPR Sensitive</Badge>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.expiry_date && (
                            <span className="text-xs text-slate-500">Expires: {file.expiry_date}</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${API_URL}/api/hr/personnel-files/${file.id}/download`, '_blank')}
                            data-testid={`download-${file.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No personnel files uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HR Records Tab */}
          <TabsContent value="hr-records" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>HR Records</CardTitle>
                <CardDescription>Supervisions, appraisals, and other HR records</CardDescription>
              </CardHeader>
              <CardContent>
                {hrRecords.length > 0 ? (
                  <div className="space-y-3">
                    {hrRecords.map((record) => (
                      <div key={record.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <ClipboardList className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium capitalize">{record.template_type?.replace('_', ' ')}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <span>{getStaffName(record.staff_id)}</span>
                              <span>•</span>
                              <span>{record.conducted_date}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {record.outcome && (
                            <Badge variant={record.outcome === 'satisfactory' || record.outcome === 'good' || record.outcome === 'excellent' ? 'default' : 'destructive'}>
                              {record.outcome}
                            </Badge>
                          )}
                          {record.next_due_date && (
                            <span className="text-xs text-slate-500">Next: {record.next_due_date}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No HR records created</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>CQC Compliant HR Templates</CardTitle>
                <CardDescription>Pre-built templates compliant with CQC and UK employment law</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {defaultTemplates.map((template) => {
                    const TypeIcon = templateTypes.find(t => t.id === template.template_type)?.icon || FileText;
                    return (
                      <Card key={template.template_type} className="border-2 border-slate-100 hover:border-teal-200 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                              <TypeIcon className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{template.title}</p>
                              {template.is_cqc_compliant && (
                                <Badge variant="outline" className="mt-1 text-xs bg-green-50 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" /> CQC Compliant
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-slate-500 mb-3">{template.description}</p>
                          <p className="text-xs text-slate-400">{template.sections?.length || 0} sections</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
